/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import * as fromRoot from '../app.reducer';
import { catchError, filter, finalize, first, flatMap, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BehaviorSubject, throwError as observableThrowError } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthService } from '../auth/auth.service';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { SetAuthenticated } from '../auth/state/auth.actions';
import { ShowError } from '../layout/state/layout.actions';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  jwtHelper = new JwtHelperService();

  constructor(private store: Store<fromRoot.AppState>, private authService: AuthService) {}

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    let headers;
    if(req.url.includes('naistro.com/audio/encrypted') || req.url.includes('naistro.com/audio') || req.url.includes('naistro.com/public')) {
      headers = new HttpHeaders({});
    } else {
      headers = new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'x-api-key': environment.apiKey,
      });
    }
    return req.clone({ headers });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    return this.store.pipe(
      switchMap(() => this.authService.cognito$),
      filter(user => !!user),
      first(),
      flatMap(user => {
        const token =
          user &&
          user
            .getSignInUserSession()
            .getIdToken()
            .getJwtToken();
        if (this.jwtHelper.isTokenExpired(token)) {
          return this.doTokenRefresh(req, next);
        } else {
          // send cloned request with header to the next handler.
          return next.handle(this.addToken(req, token));
        }
      }),
    );
  }

  doTokenRefresh(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null);
      return this.authService.doRefreshToken().pipe(
        switchMap((user: CognitoUser) => {
          this.authService.setCognito(user);
          this.store.dispatch(SetAuthenticated());
          const token = user
            .getSignInUserSession()
            .getIdToken()
            .getJwtToken();
          this.tokenSubject.next(token);
          return next.handle(this.addToken(req, token));
        }),
        catchError(error => {
          console.log('[Auth Interceptor] Token not refreshed', error);
          if (error.status === 401) {
            // If there is an unauthenticated response from 'refreshToken', bad news so logout.
            this.authService.logout();
          }
          this.store.dispatch(ShowError({ message: error.message }));
          return observableThrowError(error.message);
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        }),
      );
    } else {
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => next.handle(this.addToken(req, token))),
      );
    }
  }
}
